import { type ConnectionStatus, GameSync, subscribeGame } from "@kalaporssi/client";
import { type GameStateResponse, gameSettings, type RoundResultResponse } from "@kalaporssi/shared";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { getConfig } from "./config";

export const gameKey = (code: string) => ["game", code] as const;
export const resultKey = (code: string, n: number) => ["result", code, n] as const;

/**
 * Live game state (SPEC §9, §12): GameSync fetches on every realtime signal and polls as a fallback;
 * results land in the TanStack Query cache, which the views read.
 */
export function useGame(code: string) {
  const qc = useQueryClient();
  const [connection, setConnection] = useState<ConnectionStatus>("connecting");
  const [error, setError] = useState<unknown>(null);
  const syncRef = useRef<GameSync | null>(null);

  const query = useQuery<GameStateResponse>({
    queryKey: gameKey(code),
    queryFn: () => api().getGame(code),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    const sync = new GameSync({
      fetchState: () => api().getGame(code),
      subscribe: ({ onEvent, onStatus }) => subscribeGame(getConfig().realtime, { code, onEvent, onStatus }),
      onState: (s) => {
        setError(null);
        qc.setQueryData(gameKey(code), s);
      },
      onError: setError,
      onConnection: setConnection,
      pollIntervalMs: gameSettings.clientPollIntervalSeconds * 1000,
    });
    syncRef.current = sync;
    sync.start();
    const onVisible = () => document.visibilityState === "visible" && void sync.refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      sync.stop();
      syncRef.current = null;
    };
  }, [code, qc]);

  return {
    state: query.data ?? null,
    connection,
    error,
    refresh: () => syncRef.current?.refresh(),
    /** Put a mutation's fresh state into the cache without waiting for the event. */
    accept: (s: GameStateResponse) => {
      const cur = qc.getQueryData<GameStateResponse>(gameKey(code));
      if (!cur || s.version >= cur.version) qc.setQueryData(gameKey(code), s);
    },
  };
}

/** Round results are immutable once resolved, so they are cached forever. */
export function useResult(code: string, roundNo: number | null) {
  return useQuery<RoundResultResponse>({
    queryKey: resultKey(code, roundNo ?? 0),
    queryFn: () => api().getResult(code, roundNo ?? 0),
    enabled: roundNo != null && roundNo > 0,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 2,
  });
}

export function useAllResults(code: string, upTo: number) {
  return useQueries({
    queries: Array.from({ length: Math.max(0, upTo) }, (_, i) => ({
      queryKey: resultKey(code, i + 1),
      queryFn: () => api().getResult(code, i + 1),
      staleTime: Number.POSITIVE_INFINITY,
    })),
  });
}

/** Seconds since an ISO timestamp, ticking once a second. */
export function useElapsed(iso: string | undefined): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  return iso ? Math.max(0, Math.floor((now - Date.parse(iso)) / 1000)) : 0;
}
